import type { IBackend } from '@/api/BackendApi';

import type { RecipeDto, RecipeFavoriteDto } from '@/api/ApiClient';

import { mapRecipeDtoToState, mapRecipeStateToDto } from '@/lib/types/recipeMapper';

import { log } from '@/lib/utils/logger';

export type CreateRecipePayload = {
  recipeTemplate?: string;

  recipe?: RecipeDto;

  runtime?: number;
};

/**

 * Resolves the template name to use when applying a favorite at create time.

 */

export function resolveRecipeTemplateName(
  favorite: RecipeFavoriteDto | null | undefined,

  selectedTemplateName: string | null | undefined
): string {
  const fromFavorite = favorite?.template?.trim() || favorite?.recipe?.templateName?.trim();

  const fromSelection = selectedTemplateName?.trim();

  return fromFavorite || fromSelection || '';
}

/**

 * List API returns favorites without recipe JSON (performance). Load full content by id when needed.

 * Matches legacy RecipeActivityVM._applyFavorite → favoriteRepo.get(id).

 */

export async function resolveFavoriteWithRecipe(
  backend: IBackend,

  favorite: RecipeFavoriteDto
): Promise<RecipeFavoriteDto | null> {
  if (favorite.recipe != null) {
    return favorite;
  }

  if (favorite.id == null) {
    log.warn('[resolveFavoriteWithRecipe] Favorite has no id and no recipe content');

    return null;
  }

  try {
    return await backend.recipeFavorite.get(favorite.id);
  } catch (err) {
    log.error('[resolveFavoriteWithRecipe] Failed to load favorite by id:', err);

    return null;
  }
}

/**

 * Builds create-request recipe fields from an optional favorite and selected template name.

 * When a favorite is selected, includes full recipe JSON and optional runtime from the favorite.

 * Caller must pass a favorite that already includes `recipe`, or use {@link buildCreateRecipePayloadAsync}.

 */

export function buildCreateRecipePayload(
  favorite: RecipeFavoriteDto | null | undefined,

  selectedTemplateName: string | null | undefined
): CreateRecipePayload {
  const templateName = resolveRecipeTemplateName(favorite, selectedTemplateName);

  if (!templateName) {
    return {};
  }

  if (!favorite?.recipe) {
    return { recipeTemplate: templateName };
  }

  const recipeState = mapRecipeDtoToState(favorite.recipe);

  const recipeDto = mapRecipeStateToDto(recipeState);

  recipeDto.templateName = templateName;

  const payload: CreateRecipePayload = {
    recipeTemplate: templateName,

    recipe: recipeDto,
  };

  if (favorite.recipe.calculatedRuntime != null) {
    payload.runtime = favorite.recipe.calculatedRuntime;
  }

  return payload;
}

/**

 * Like {@link buildCreateRecipePayload} but fetches recipe content via GET when the list item has no recipe.

 */

export async function buildCreateRecipePayloadAsync(
  backend: IBackend,

  favorite: RecipeFavoriteDto | null | undefined,

  selectedTemplateName: string | null | undefined
): Promise<CreateRecipePayload> {
  if (!favorite) {
    return buildCreateRecipePayload(null, selectedTemplateName);
  }

  const resolved = await resolveFavoriteWithRecipe(backend, favorite);

  return buildCreateRecipePayload(resolved, selectedTemplateName);
}

/**

 * Loads recipe favorites for a processing step / technique / instrument / template (same API as detail view).

 * Note: list items do not include recipe JSON; use {@link resolveFavoriteWithRecipe} before apply.

 */

export async function fetchRecipeFavoritesForProcessing(
  backend: IBackend,

  technique: string | null | undefined,

  instrument: string | null | undefined,

  templateName: string | null | undefined,

  activityType: string | null | undefined
): Promise<RecipeFavoriteDto[]> {
  if (!templateName?.trim()) {
    return [];
  }

  return backend.recipeFavorite.list(
    technique ?? null,

    instrument ?? null,

    templateName,

    activityType ?? null
  );
}
